/**
 * What the Study Build rows and the requirements and design document panels
 * show when a document has sections (or table rows) that could not be
 * generated. Pure, so it can be tested without a browser.
 *
 * Contract: incomplete_contract.md (2026-10-14). The backend stores
 * generationStatus / failedSectionCount on the document, status
 * "generation_failed" on a failed section or table row, and failedAreas on
 * the System Requirements document. A document stored before this change has
 * none of those fields and is shown exactly as before.
 */
import { isDocumentIncomplete } from './documentReadiness';
import { FAILED_LABEL } from './sectionStatus';

export interface StudyBuildBadgeInput {
  /** The row is generating right now (marker or local click). */
  generating: boolean;
  /** The whole document failed (_generationError). */
  failed: boolean;
  /** The document exists and has a generation time. */
  generated: boolean;
  /** The stored document, for generationStatus / failedSectionCount / sections. */
  doc?: Record<string, unknown> | null;
}

export type StudyBuildBadgeKind = 'generating' | 'failed' | 'incomplete' | 'generated' | 'none';

export interface StudyBuildBadge {
  kind: StudyBuildBadgeKind;
  /** The pill text for incomplete; other kinds keep their existing wording. */
  label: string;
  /** Tooltip for the incomplete pill. */
  tooltip?: string;
  /** How many sections could not be generated, when the document says. */
  failedCount?: number;
}

export const INCOMPLETE_LABEL = 'Incomplete';

function failedCountOf(doc: Record<string, unknown> | null | undefined): number | undefined {
  if (!doc) return undefined;
  // failedSectionCount counts failed table rows too, so it wins when present.
  const n = doc.failedSectionCount;
  if (typeof n === 'number' && n > 0) return n;
  const sections = Array.isArray(doc.sections) ? (doc.sections as Array<{ status?: unknown }>) : [];
  const fromSections = sections.filter((s) => s?.status === 'generation_failed' || s?.status === 'failed').length;
  return fromSections > 0 ? fromSections : undefined;
}

/**
 * isDocumentIncomplete lets per-section statuses win, which misses a document
 * whose failures are all at ROW level (every section "generated", rows
 * "generation_failed"). The stored summary counts too.
 */
function isIncomplete(doc: Record<string, unknown> | null | undefined): boolean {
  if (isDocumentIncomplete(doc)) return true;
  if (!doc) return false;
  if (doc.generationStatus === 'incomplete') return true;
  return typeof doc.failedSectionCount === 'number' && doc.failedSectionCount > 0;
}

/** "3 sections could not be generated. Use Regenerate to try again." */
export function incompleteTooltip(count: number | undefined): string {
  if (count === 1) return '1 section could not be generated. Use Regenerate to try again.';
  if (typeof count === 'number' && count > 1) {
    return `${count} sections could not be generated. Use Regenerate to try again.`;
  }
  return 'Some sections could not be generated. Use Regenerate to try again.';
}

export function studyBuildBadge(input: StudyBuildBadgeInput): StudyBuildBadge {
  if (input.generating) return { kind: 'generating', label: 'Generating' };
  if (input.failed) return { kind: 'failed', label: 'Generation failed' };
  if (!input.generated) return { kind: 'none', label: '' };
  if (isIncomplete(input.doc)) {
    const failedCount = failedCountOf(input.doc);
    return { kind: 'incomplete', label: INCOMPLETE_LABEL, tooltip: incompleteTooltip(failedCount), failedCount };
  }
  return { kind: 'generated', label: 'Generated' };
}

/* ---------- Table rows (Interface Features units, DB Physical Model tables) ---------- */

export const ROW_FAILED_LABEL = FAILED_LABEL;

export function isRowFailed(row: { status?: unknown } | null | undefined): boolean {
  const s = String(row?.status ?? '');
  return s === 'generation_failed' || s === 'failed';
}

export function failedRowCount(rows: ReadonlyArray<{ status?: unknown }> | null | undefined): number {
  return (rows ?? []).filter(isRowFailed).length;
}

/**
 * The rows a panel previews: the first `limit`, plus every failed row beyond
 * them, so a failure is never hidden behind "showing first 5".
 */
export function previewRows<T extends { status?: unknown }>(rows: ReadonlyArray<T>, limit: number): T[] {
  const head = rows.slice(0, limit);
  const tail = rows.slice(limit).filter(isRowFailed);
  return [...head, ...tail];
}

/* ---------- System Requirements: failed requirement areas ---------- */

export interface FailedArea {
  area?: unknown;
  failureReasons?: unknown;
}

/** The areas whose requirements could not be generated, de-duplicated, in order. */
export function failedAreaNames(failedAreas: unknown): string[] {
  if (!Array.isArray(failedAreas)) return [];
  const out: string[] = [];
  for (const a of failedAreas as FailedArea[]) {
    const name = typeof a?.area === 'string' ? a.area.trim() : '';
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

/** One plain sentence naming them; undefined when nothing failed. */
export function failedAreasLine(failedAreas: unknown): string | undefined {
  const names = failedAreaNames(failedAreas);
  if (!names.length) return undefined;
  return names.length === 1
    ? `Requirements for this area could not be generated: ${names[0]}.`
    : `Requirements for these areas could not be generated: ${names.join(', ')}.`;
}
